import { useState, useCallback } from 'react';
import { Icon } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { TriggerType } from './TriggerType';
import {
  EmailTriggerDialog,
  type IEmailTriggerSaveData,
  type IEmailFilters,
  DEFAULT_FILTERS,
} from '../EmailTriggerDialog';
import type { TInputMappings } from '../WebhookTriggerDialog/types';
import { useCreateTrigger } from '../WebhookTriggerDialog';
import { useRetrieveAllWorkspaceBookConnections } from '@/generated/hooks/bdk/v2/services/library';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';
import type { IEmailTriggerProps } from './types';
import {
  EMAIL_BOOK_NAME,
  EMAIL_TRIGGER_ID,
  EMAIL_CONNECTION_ID,
} from '../EmailTriggerDialog/constants';
import { createEmailTriggerConfigurationAndFilters } from './utils';

/**
 * EmailTrigger Component
 *
 * Handles the email trigger type selection and configuration.
 * Encapsulates the email dialog and trigger creation logic.
 *
 * Flow:
 * 1. User clicks the email trigger type button
 * 2. Opens EmailTriggerDialog for configuration
 * 3. On save, creates trigger instance in BDK (with email filters as configuration)
 * 4. Sets automations for the trigger instance in BDK
 * 5. Then creates automation trigger in Grimoire with input mappings
 *
 * @param props - Component props
 * @returns TriggerType component for email with integrated dialog
 */
export function EmailTrigger({
  inputSpecs,
  automationId,
  automationName,
  isDraft = true,
  presentation = 'card',
  onOpen,
  open,
  onOpenChange,
  hideTrigger = false,
}: IEmailTriggerProps) {
  const { t } = useTranslation('automation-inputs');

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Email dialog state
  const {
    value: isEmailDialogOpen,
    setTrue: openEmailDialog,
    setValue: setIsEmailDialogOpen,
  } = useBoolean(false);

  // Controlled state for the dialog
  const [currentStep, setCurrentStep] = useState(1);
  const [filters, setFilters] = useState<IEmailFilters>(DEFAULT_FILTERS);
  const [inputMappings, setInputMappings] = useState<TInputMappings>({});

  /**
   * Resets dialog state to initial values
   */
  const resetDialogState = useCallback(() => {
    setCurrentStep(1);
    setFilters(DEFAULT_FILTERS);
    setInputMappings({});
  }, []);

  /**
   * Handles dialog open state changes.
   * Resets state when dialog closes.
   */
  const handleDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      setIsEmailDialogOpen(nextOpen);
      onOpenChange?.(nextOpen);
      if (!nextOpen) {
        resetDialogState();
      }
    },
    [setIsEmailDialogOpen, onOpenChange, resetDialogState],
  );

  const handleOpen = useCallback(() => {
    onOpen?.();
    openEmailDialog();
    onOpenChange?.(true);
  }, [onOpen, onOpenChange, openEmailDialog]);

  // Fetch the email book connection to get the version tied to the available connection
  const { data: emailConnectionsData } = useRetrieveAllWorkspaceBookConnections(
    organizationId,
    workspaceId,
    {
      page_size: 600,
      filter: FilterBuilder.create()
        .equals('book_name', EMAIL_BOOK_NAME)
        .build(),
    },
    {
      query: {
        enabled: !!organizationId && !!workspaceId,
      },
    },
  );

  const emailConnection = emailConnectionsData?.data?.book_connections?.find(
    c => c.connection_id === EMAIL_CONNECTION_ID,
  );
  const emailBookVersion = emailConnection?.book_version ?? '';

  // Use the generic createTrigger hook
  const { createTrigger, isLoading } = useCreateTrigger({
    automationId,
    isDraft,
    onSuccess: () => handleDialogOpenChange(false),
    toastMessages: {
      success: t('emailDialog.toast.success'),
      error: t('emailDialog.toast.error'),
      errorNoTriggerId: t('emailDialog.toast.error'),
    },
    sentryTags: {
      component: 'EmailTrigger',
      action: 'handleEmailTriggerSave',
    },
  });

  /**
   * Handles email trigger save.
   * Creates trigger instance in BDK and automation trigger in Grimoire.
   *
   * @param data - Email trigger save data containing mailbox, filters, and mapping details
   */
  const handleEmailTriggerSave = useCallback(
    async (data: IEmailTriggerSaveData) => {
      const { inputMappings, filters, mailboxAddress } = data;

      // Validate email book version is available
      if (!emailBookVersion) {
        return;
      }

      // Create email trigger configuration and filter expression
      const { configuration, filter_expression } =
        createEmailTriggerConfigurationAndFilters(mailboxAddress, filters);

      // Call the generic createTrigger
      await createTrigger({
        bookName: EMAIL_BOOK_NAME,
        bookVersion: emailBookVersion,
        connectionId: EMAIL_CONNECTION_ID,
        triggerId: EMAIL_TRIGGER_ID,
        configuration,
        filterExpression: filter_expression,
        inputMappings,
      });
    },
    [emailBookVersion, createTrigger],
  );

  return (
    <>
      {!hideTrigger && (
        <TriggerType
          icon={
            <Icon
              type="Mail"
              size="lg"
              strokeWidth={1.5}
              className="text-ring"
            />
          }
          title={t('triggerTypes.email.title')}
          description={t('triggerTypes.email.description')}
          onClick={handleOpen}
          presentation={presentation}
        />
      )}

      {/* Email Trigger Dialog */}
      <EmailTriggerDialog
        isOpen={open ?? isEmailDialogOpen}
        onOpenChange={handleDialogOpenChange}
        inputSpecs={inputSpecs}
        automationId={automationId}
        automationName={automationName}
        isDraft={isDraft}
        onSave={handleEmailTriggerSave}
        isLoading={isLoading}
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        filters={filters}
        onFiltersChange={setFilters}
        inputMappings={inputMappings}
        onInputMappingsChange={setInputMappings}
      />
    </>
  );
}
